import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLanguage } from '../lib/language'
import { supabase } from '../lib/supabase'

// ── SERVICE CATEGORIES ───────────────────────────────────────
const SERVICE_CATEGORIES = [
  {
    id: 'affidavits', icon: '📋',
    name_en: 'Affidavits & Sworn Statements',
    name_es: 'Declaraciones Juradas',
    desc_en: 'Need to put something in writing and make it official? We witness it, certify it, and make it legal — simple and stress-free.',
    desc_es: 'Documentos donde juras bajo juramento que algo es verdad',
    services_en: ['Affidavit (General)','Acknowledgement','Jurat / Oath','Name Change Affidavit','Residency Affidavit','Identity Affidavit','Lost Documents Affidavit','Financial Affidavit','Heirship Affidavit','Estate Affidavit','Certified Identity Statement','Certified Residency Statement'],
    services_es: ['Declaración Jurada (General)','Reconocimiento','Juramento','Declaración de Cambio de Nombre','Declaración de Residencia','Declaración de Identidad','Declaración de Documentos Perdidos','Declaración Financiera','Declaración de Heredero','Declaración de Patrimonio','Declaración Certificada de Identidad','Declaración Certificada de Residencia'],
    plains_en: ['A sworn written statement about any topic','Confirming you signed of your own free will','Swearing under oath that a document is true','Confirming your legal name has changed','Proving where you live','Confirming who you are','Swearing that an original document was lost','A sworn statement about your finances','Proving you are an heir to an estate','Sworn statement related to an estate','Official statement confirming your identity','Official statement confirming your address'],
    plains_es: ['Una declaración escrita jurada','Confirmar que firmaste por voluntad propia','Jurar que un documento es verdadero','Confirmar que tu nombre legal ha cambiado','Probar dónde vives','Confirmar quién eres','Jurar que un documento se perdió','Una declaración jurada sobre tus finanzas','Probar que eres heredero','Declaración jurada de patrimonio','Declaración oficial de identidad','Declaración oficial de dirección'],
  },
  {
    id: 'family', icon: '👨‍👩‍👧',
    name_en: 'Family & Personal Documents',
    name_es: 'Documentos Familiares y Personales',
    desc_en: 'From permission slips to adoption papers — whatever your family needs notarized, we come to you with care and patience.',
    desc_es: 'Documentos para asuntos familiares y personales',
    services_en: ['Parental Consent Form','Tattoo Parental Consent (Minor)','Travel Consent Letter for Minors','Adoption Documents','Guardianship / Custodian Affidavit','School Documents','Proof of Life / Life Certificate'],
    services_es: ['Formulario de Consentimiento Parental','Consentimiento para Tatuaje (Menor)','Carta de Consentimiento de Viaje','Documentos de Adopción','Declaración de Tutela / Custodia','Documentos Escolares','Certificado de Vida'],
    plains_en: ['Permission from a parent for a child\'s activity','Parent\'s written permission for a minor to get a tattoo','Permission for a child to travel without both parents','Legal paperwork related to adopting a child','Documents related to who takes care of a child','Any school-related paperwork needing notarization','Confirming someone is alive — often needed for pensions'],
    plains_es: ['Permiso de un padre para actividad de un menor','Permiso escrito del padre para que un menor se tatúe','Permiso para que un menor viaje sin ambos padres','Papeleo legal relacionado con la adopción','Documentos relacionados con tutela','Cualquier papeleo escolar que necesite notarización','Confirmar que alguien está vivo'],
  },
  {
    id: 'legal', icon: '⚖️',
    name_en: 'Legal & Court Documents',
    name_es: 'Documentos Legales y Judiciales',
    desc_en: 'Powers of attorney, living wills, court paperwork — we handle the important stuff so you don\'t have to figure it out alone.',
    desc_es: 'Documentos relacionados con asuntos legales y judiciales',
    services_en: ['Power of Attorney','Advance Health Directive / Living Will','Living Will','Court Documents','Hold Harmless Agreement','Consent & Release Form','Private Party Agreement'],
    services_es: ['Poder Notarial','Directiva Anticipada de Salud','Testamento Vital','Documentos Judiciales','Acuerdo de Indemnización','Formulario de Consentimiento','Acuerdo entre Particulares'],
    plains_en: ['Giving someone legal authority to act on your behalf','Your wishes for medical care if you can\'t speak for yourself','Written instructions for end-of-life medical care','Any paperwork filed with or from a court','Agreement releasing someone from responsibility','Giving permission and releasing liability','A contract between two private individuals'],
    plains_es: ['Dar autoridad legal a alguien para actuar en tu nombre','Tus deseos de atención médica','Instrucciones para atención médica al final de la vida','Cualquier papeleo de un tribunal','Acuerdo que libera responsabilidad','Dar permiso y liberar responsabilidad','Un contrato entre dos personas privadas'],
  },
  {
    id: 'medical', icon: '🏥',
    name_en: 'Medical & Healthcare',
    name_es: 'Médico y Salud',
    desc_en: 'Authorizing someone to make medical decisions or access your records? We make sure it\'s done right and recognized everywhere.',
    desc_es: 'Documentos relacionados con tu salud',
    services_en: ['Medical Authorization Form','Insurance Claims Form'],
    services_es: ['Formulario de Autorización Médica','Formulario de Reclamo de Seguro'],
    plains_en: ['Allowing someone to make medical decisions or access your records','Paperwork for filing an insurance claim needing notarization'],
    plains_es: ['Permitir que alguien tome decisiones médicas','Papeleo para un reclamo de seguro'],
  },
  {
    id: 'financial', icon: '🏦',
    name_en: 'Financial & Banking',
    name_es: 'Financiero y Bancario',
    desc_en: 'Bank forms, pension documents, promissory notes — we notarize your financial paperwork quickly and accurately.',
    desc_es: 'Documentos relacionados con finanzas y bancos',
    services_en: ['Banking Forms','Pension / Retirement Forms','Promissory Note (Non-Real Estate)','Bank Safe Deposit Box Inventory / Drilling'],
    services_es: ['Formularios Bancarios','Formularios de Pensión / Jubilación','Pagaré (No Inmobiliario)','Inventario de Caja de Seguridad'],
    plains_en: ['Bank paperwork that requires a notary\'s seal','Documents related to your retirement or pension','A written promise to repay a loan (not for a house)','Opening or documenting the contents of a safe deposit box'],
    plains_es: ['Papeleo bancario que requiere sello notarial','Documentos de jubilación o pensión','Una promesa escrita de pagar un préstamo','Documentar el contenido de una caja de seguridad'],
  },
  {
    id: 'realestate', icon: '🏠',
    name_en: 'Real Estate & Property',
    name_es: 'Bienes Raíces y Propiedad',
    desc_en: 'Buying, selling, or renting? We notarize your property documents on your schedule — wherever you need us.',
    desc_es: 'Documentos relacionados con propiedades y alquileres',
    services_en: ['Lease Agreement','Rental Verification Form','Bill of Sale'],
    services_es: ['Contrato de Arrendamiento','Formulario de Verificación de Alquiler','Factura de Venta'],
    plains_en: ['A rental contract between landlord and tenant','Confirming where you rent or your rental history','Proof of purchase when selling or buying something'],
    plains_es: ['Un contrato de alquiler entre propietario e inquilino','Confirmar historial de alquiler','Prueba de compra al vender o comprar algo'],
  },
  {
    id: 'government', icon: '🏛️',
    name_en: 'Employment & Government',
    name_es: 'Empleo y Gobierno',
    desc_en: 'I-9s, passport forms, DMV paperwork, employment verification — we handle official documents without the headache.',
    desc_es: 'Documentos para trabajo y agencias gubernamentales',
    services_en: ['Employment Verification Form','I-9 Form','DMV Forms','Passport Forms','VIN / Vehicle Identification Statement'],
    services_es: ['Formulario de Verificación de Empleo','Formulario I-9','Formularios del DMV','Formularios de Pasaporte','Declaración de VIN / Vehículo'],
    plains_en: ['Confirming your employment status or history','Work eligibility verification form required by employers','Any Department of Motor Vehicles paperwork needing notarization','Passport application or renewal documents','Official statement about a vehicle\'s identification number'],
    plains_es: ['Confirmar tu estado de empleo','Formulario de elegibilidad de trabajo','Papeleo del Departamento de Vehículos Motorizados','Documentos de pasaporte','Declaración del número de identificación de un vehículo'],
  },
  {
    id: 'dealership', icon: '🚗',
    name_en: 'Car Dealership Services',
    name_es: 'Servicios para Concesionarios',
    desc_en: 'Running a dealership? We handle your entire signing package — professionally, accurately, and on your timeline.',
    desc_es: 'Servicios notariales para concesionarios',
    services_en: ['Vehicle Purchase Contract','Title Transfer','Lease Agreement (Auto)','Finance Documents Package','Power of Attorney (Vehicle)','Odometer Disclosure Statement','Bill of Sale (Vehicle)','Dealer Reassignment Form'],
    services_es: ['Contrato de Compra de Vehículo','Transferencia de Título','Contrato de Arrendamiento (Auto)','Paquete de Documentos de Financiamiento','Poder Notarial (Vehículo)','Declaración de Odómetro','Factura de Venta (Vehículo)','Formulario de Reasignación'],
    plains_en: ['Notarizing the sale agreement between buyer and dealer','Official transfer of vehicle ownership','Notarizing a vehicle lease contract','Full package of loan and finance docs','Authorizing someone to handle vehicle transactions','Federal form disclosing mileage at time of sale','Proof of vehicle purchase for registration','Used when a dealer sells a vehicle from another dealer'],
    plains_es: ['Notarización del acuerdo de venta','Transferencia oficial de propiedad del vehículo','Notarización de contrato de arrendamiento','Paquete de documentos de financiamiento','Autorizar transacciones de vehículo','Formulario de kilometraje al momento de la venta','Prueba de compra de vehículo','Para vehículos de otro concesionario'],
  },
  {
    id: 'international', icon: '🌍',
    name_en: 'International & Apostille',
    name_es: 'Internacional y Apostilla',
    desc_en: 'Need your document recognized in another country? We handle apostilles and international certifications so you\'re covered worldwide.',
    desc_es: 'Documentos para uso en otros países',
    services_en: ['Apostille','International Document Statement','Certified Copies'],
    services_es: ['Apostilla','Declaración de Documento Internacional','Copias Certificadas'],
    plains_en: ['Special certification to make your document valid in another country','A sworn statement for use in a foreign country','Official copies of original documents certified as true'],
    plains_es: ['Certificación para hacer válido tu documento en otro país','Una declaración jurada para uso en otro país','Copias oficiales de documentos originales certificadas'],
  },
  {
    id: 'signature', icon: '✍️',
    name_en: 'Additional Witness Services',
    name_es: 'Servicios de Testigo Adicional',
    desc_en: 'Some documents require an additional witness beyond the notary. We provide that too — so you never have to scramble to find someone last minute.',
    desc_es: 'Tener tu firma oficialmente atestiguada',
    services_en: ['Signature Witnessing','Prenuptial / Postnuptial Agreement'],
    services_es: ['Testimonio de Firma','Acuerdo Prenupcial / Postnupcial'],
    plains_en: ['A notary watches you sign and verifies your identity','Legal agreement between spouses about finances'],
    plains_es: ['Un notario observa tu firma y verifica tu identidad','Acuerdo legal entre cónyuges sobre finanzas'],
  },
  {
    id: 'wedding', icon: '💍',
    name_en: 'Weddings & Ceremonies',
    name_es: 'Bodas y Ceremonias',
    desc_en: 'Say \'I do\' with a certified notary officiating your special day. Available in Florida, Maine, Montana, Nevada, South Carolina, and Tennessee.',
    desc_es: 'Ceremonias de matrimonio oficiadas por un notario',
    services_en: ['Wedding Ceremony Officiation'],
    services_es: ['Oficialización de Ceremonia de Boda'],
    plains_en: ['A notary officiates your wedding ceremony (Florida, Maine, Montana, Nevada, South Carolina, and Tennessee only)'],
    plains_es: ['Un notario oficia tu ceremonia de boda (Florida, Maine, Montana, Nevada, Carolina del Sur y Tennessee)'],
  },
]

// ── ALL HARDCODED SERVICE NAMES (normalized) for dedup check ──
const HARDCODED_SERVICES = new Set(
  SERVICE_CATEGORIES.flatMap(c => c.services_en).map(s => s.toLowerCase().trim())
)

// ── ALL HARDCODED CATEGORY NAMES (normalized) for dedup check ──
const HARDCODED_CATEGORY_NAMES = new Set(
  SERVICE_CATEGORIES.map(c => c.name_en.toLowerCase().trim())
)

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

  /* ── ACCESSIBILITY ── */
  .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  .skip-link { position:absolute; top:-100px; left:16px; background:#C9A84C; color:#0D1B3E; padding:10px 20px; border-radius:8px; font-size:14px; font-weight:700; text-decoration:none; z-index:9999; transition:top 0.2s; }
  .skip-link:focus { top:16px; }
  :focus-visible { outline:3px solid #C9A84C; outline-offset:3px; border-radius:4px; }

  /* ── LAYOUT ── */
  .svc-page { min-height:100vh; background:linear-gradient(180deg,#0D1B3E 0%,#1A2F5E 100%); color:#fff; font-family:'DM Sans',sans-serif; }

  /* ── NAV ── */
  .svc-nav { position:fixed; top:0; left:0; right:0; z-index:100; padding:16px 48px; display:flex; align-items:center; justify-content:space-between; background:linear-gradient(to bottom,rgba(0,0,0,0.7),transparent); transition:background 0.4s; }
  .svc-nav.scrolled { background:rgba(13,27,62,0.97); backdrop-filter:blur(8px); }
  .svc-nav-brand { text-decoration:none; }
  .svc-nav-name { font-family:'Cormorant Garamond',serif; font-size:22px; color:#C9A84C; display:block; }
  .svc-nav-sub { font-size:8px; color:rgba(201,168,76,0.55); letter-spacing:0.15em; display:block; margin-top:2px; }
  .svc-nav-links { display:flex; gap:24px; align-items:center; }
  .svc-nav-link { color:rgba(255,255,255,0.75); font-size:12px; text-decoration:none; letter-spacing:0.05em; transition:color 0.2s; }
  .svc-nav-link:hover { color:#C9A84C; }
  .svc-nav-link.active { color:#C9A84C; font-weight:500; }
  @media(max-width:640px){ .svc-nav { padding:12px 20px; } .svc-nav-name { font-size:16px; } .svc-nav-links .svc-nav-link { display:none; } }
  @media(max-width:640px){ .svc-hero { padding:100px 20px 48px; } .svc-hero h1 { font-size:28px; } }
  @media(max-width:380px){ .svc-hero { padding:90px 16px 40px; } .svc-hero h1 { font-size:24px; } }

  /* ── HERO ── */
  .svc-hero { padding:120px 48px 64px; text-align:center; border-bottom:1px solid rgba(255,255,255,0.06); }
  .svc-hero-eyebrow { font-size:10px; letter-spacing:0.2em; color:#C9A84C; margin-bottom:14px; }
  .svc-hero-rule { width:40px; height:1px; background:#C9A84C; margin:0 auto 22px; }
  .svc-hero h1 { font-family:'Cormorant Garamond',serif; font-size:38px; font-weight:400; color:#fff; margin-bottom:14px; line-height:1.2; }
  .svc-hero h1 span { color:#C9A84C; }
  .svc-hero-sub { font-size:14px; color:rgba(255,255,255,0.55); max-width:520px; margin:0 auto 36px; line-height:1.85; }

  /* ── PATHWAYS ── */
  .svc-paths { display:grid; grid-template-columns:1fr 1fr; gap:16px; max-width:560px; margin:0 auto 20px; }
  @media(max-width:580px){ .svc-paths { grid-template-columns:1fr; } }
  .svc-path-card { background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:14px; padding:20px; text-align:center; transition:all 0.2s; }
  .svc-path-card:hover { border-color:rgba(201,168,76,0.4); background:rgba(201,168,76,0.06); }
  .svc-path-h3 { font-size:13px; color:rgba(255,255,255,0.5); margin-bottom:10px; font-weight:400; letter-spacing:0.04em; }
  .svc-path-btn { display:inline-block; padding:11px 24px; border-radius:10px; font-size:13px; font-weight:600; cursor:pointer; border:none; font-family:inherit; letter-spacing:0.04em; transition:all 0.2s; text-decoration:none; }
  .svc-path-btn.guest { background:linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color:#fff; border:1px solid rgba(201,168,76,0.5); box-shadow:0 4px 12px rgba(13,27,62,0.3); }
  .svc-path-btn.guest:hover { transform:translateY(-2px); box-shadow:0 6px 18px rgba(13,27,62,0.4); }
  .svc-path-btn.signin { background:#0D1B3E; color:#C9A84C; border:1.5px solid rgba(201,168,76,0.45); box-shadow:0 2px 8px rgba(13,27,62,0.2); }
  .svc-path-btn.signin:hover { background:linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color:#fff; border-color:transparent; transform:translateY(-2px); box-shadow:0 6px 18px rgba(13,27,62,0.4); }

  /* ── MAIN CONTENT ── */
  .svc-main { max-width:860px; margin:0 auto; padding:48px 24px 140px; }
  .svc-instruction { font-size:13px; color:rgba(255,255,255,0.45); margin-bottom:24px; text-align:center; line-height:1.7; }

  /* ── CATEGORIES — ALL cards use same dark navy style ── */
  .svc-cat { border:1px solid rgba(255,255,255,0.08); border-radius:12px; margin-bottom:10px; overflow:hidden; transition:border-color 0.2s; background:rgba(255,255,255,0.02); }
  .svc-cat:hover { border-color:rgba(201,168,76,0.25); }
  .svc-cat.open { border-color:rgba(201,168,76,0.45); }
  .svc-cat-btn { display:flex; align-items:center; gap:14px; padding:18px 22px; background:transparent; border:none; width:100%; cursor:pointer; text-align:left; transition:background 0.2s; font-family:inherit; }
  .svc-cat-btn:hover { background:rgba(255,255,255,0.04); }
  .svc-cat.open .svc-cat-btn { background:rgba(201,168,76,0.07); }
  .svc-cat-icon { font-size:26px; flex-shrink:0; }
  .svc-cat-info { flex:1; min-width:0; }
  .svc-cat-name { font-size:16px; font-weight:500; color:#fff; margin-bottom:2px; display:block; }
  .svc-cat-desc { font-size:12px; color:rgba(255,255,255,0.4); display:block; }
  .svc-cat-count { font-size:11px; color:rgba(201,168,76,0.7); background:rgba(201,168,76,0.1); padding:4px 12px; border-radius:20px; flex-shrink:0; white-space:nowrap; }
  .svc-cat-arrow { font-size:14px; color:rgba(255,255,255,0.3); flex-shrink:0; transition:transform 0.25s; }
  .svc-cat.open .svc-cat-arrow { transform:rotate(180deg); }

  /* ── MOBILE ACCORDION — prevent text overlap ── */
  @media(max-width:480px){
    .svc-cat-btn { padding:14px 16px; gap:10px; flex-wrap:nowrap; }
    .svc-cat-icon { font-size:20px; }
    .svc-cat-name { font-size:13px; }
    .svc-cat-desc { font-size:11px; white-space:normal; word-break:break-word; }
    .svc-cat-count { font-size:10px; padding:3px 8px; }
    .svc-cat-info { overflow:hidden; }
  }

  /* ── SERVICES LIST ── */
  .svc-list { display:none; padding:10px 22px 18px; }
  .svc-cat.open .svc-list { display:block; }
  .svc-item { display:flex; align-items:center; justify-content:space-between; gap:14px; padding:13px 16px; margin-bottom:6px; border-radius:10px; background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.05); transition:all 0.2s; flex-wrap:wrap; }
  .svc-item:hover { background:rgba(201,168,76,0.06); border-color:rgba(201,168,76,0.2); }
  .svc-item.added { background:rgba(45,122,79,0.12); border-color:rgba(45,122,79,0.35); }
  .svc-item-body { flex:1; min-width:0; }
  .svc-item-name { font-size:14px; color:#fff; font-weight:400; margin-bottom:3px; display:block; }
  .svc-item-plain { font-size:11px; color:rgba(255,255,255,0.35); line-height:1.5; display:block; }
  .svc-add-btn { flex-shrink:0; padding:7px 16px; border-radius:20px; font-size:11px; font-weight:700; cursor:pointer; border:1.5px solid; font-family:inherit; letter-spacing:0.05em; transition:all 0.2s; white-space:nowrap; }
  .svc-add-btn.default { background:transparent; color:#C9A84C; border-color:rgba(201,168,76,0.4); }
  .svc-add-btn.default:hover { background:rgba(201,168,76,0.15); }
  .svc-add-btn.added { background:#2D7A4F; color:#fff; border-color:#2D7A4F; }
  .svc-add-btn.added:hover { background:#235F3C; }
  @media(max-width:480px){
    .svc-item { flex-direction:column; align-items:flex-start; gap:10px; }
    .svc-add-btn { width:100%; text-align:center; justify-content:center; }
    .svc-cat-btn { padding:14px 16px; }
    .svc-cat-name { font-size:14px; }
  }

  /* ── STICKY CART BAR ── */
  .svc-cart-bar { position:fixed; bottom:0; left:0; right:0; z-index:200; transform:translateY(100%); transition:transform 0.35s cubic-bezier(0.175,0.885,0.32,1.275); }
  .svc-cart-bar.visible { transform:translateY(0); }
  .svc-cart-bar-inner { background:linear-gradient(135deg,#0D1B3E,#1A3A5E); border-top:2px solid #C9A84C; padding:14px 24px; display:flex; align-items:center; justify-content:space-between; gap:16px; box-shadow:0 -8px 32px rgba(0,0,0,0.4); }
  .svc-cart-info { display:flex; align-items:center; gap:12px; }
  .svc-cart-count { background:#C9A84C; color:#0D1B3E; font-size:13px; font-weight:800; width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .svc-cart-text { font-size:13px; color:rgba(255,255,255,0.8); }
  .svc-cart-text strong { color:#fff; }
  .svc-cart-review-btn { background:linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color:#fff; border:none; padding:12px 28px; border-radius:10px; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; letter-spacing:0.04em; box-shadow:0 4px 12px rgba(13,27,62,0.3); transition:all 0.2s; white-space:nowrap; flex-shrink:0; }
  .svc-cart-review-btn:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(13,27,62,0.4); }
  @media(max-width:480px){ .svc-cart-bar-inner { padding:12px 16px; } .svc-cart-review-btn { padding:10px 18px; font-size:13px; } }

  /* ── FOOTER ── */
  .svc-footer { background:#0D1B3E; border-top:1px solid rgba(201,168,76,0.15); padding:32px 48px 28px; text-align:center; }
  .svc-footer-logo { display:inline-flex; align-items:center; gap:12px; text-decoration:none; margin-bottom:22px; }
  .svc-footer-logo-circle { width:44px; height:44px; border-radius:50%; border:1px solid rgba(201,168,76,0.3); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .svc-footer-logo-circle img { width:40px; height:40px; object-fit:contain; }
  .svc-footer-brand-name { font-family:'Cormorant Garamond',serif; font-size:14px; color:rgba(255,255,255,0.55); display:block; text-align:left; }
  .svc-footer-brand-sub { font-size:8px; color:rgba(201,168,76,0.85); letter-spacing:0.12em; display:block; text-align:left; margin-top:2px; }
  .svc-footer-links { display:flex; gap:22px; justify-content:center; flex-wrap:wrap; margin-bottom:22px; }
  .svc-footer-links a { font-size:11px; color:rgba(255,255,255,0.3); text-decoration:none; transition:color 0.2s; }
  .svc-footer-links a:hover { color:#C9A84C; }
  .svc-footer-bottom { border-top:1px solid rgba(255,255,255,0.06); padding-top:16px; }
  .svc-footer-copy { font-size:10px; color:rgba(255,255,255,0.65); margin-bottom:6px; }
  .svc-footer-veteran { font-size:11px; color:rgba(201,168,76,0.5); }
  @media(max-width:520px){ .svc-footer { padding:28px 20px 20px; } }

  /* ── BACK TO TOP ── */
  .svc-backtop { position:fixed; bottom:80px; right:20px; width:44px; height:44px; border-radius:50%; background:#C9A84C; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; box-shadow:0 4px 16px rgba(0,0,0,0.3); transition:all 0.3s; opacity:0; pointer-events:none; z-index:150; }
  .svc-backtop.show { opacity:1; pointer-events:auto; }
  .svc-backtop.cart-visible { bottom:90px; }
  .svc-backtop:hover { transform:translateY(-4px); }
`

export default function ServicesPage() {
  const { t, language }   = useLanguage()
  const navigate          = useNavigate()
  const isEs              = language === 'es'
  const [scrolled,   setScrolled]   = useState(false)
  const [showTop,    setShowTop]    = useState(false)
  const [openCats,   setOpenCats]   = useState({})
  const [cart,       setCart]       = useState([])
  const [categories, setCategories] = useState(SERVICE_CATEGORIES)
  const [dbServices, setDbServices] = useState([])

  // ── SCROLL ──────────────────────────────────────────────────
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60)
      setShowTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // ── SYNC WITH ADMIN DB ──────────────────────────────────────
  // ✅ FIX: Filter out DB categories that duplicate hardcoded ones
  // This prevents the "Sesame Street" effect where individual services
  // from the DB render as orphan single-item accordion cards
  useEffect(() => {
    supabase.from('service_categories').select('*').order('name_en')
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          const dbCats = data
            .map(c => ({
              id: `db_${c.id}`, icon: c.icon || '📋',
              name_en: c.name_en, name_es: c.name_es || c.name_en,
              desc_en: c.desc_en || '', desc_es: c.desc_es || c.desc_en || '',
              services_en: c.services_en || [], services_es: c.services_es || c.services_en || [],
              plains_en: c.plains_en || [], plains_es: c.plains_es || c.plains_en || [],
              isFromDB: true,
            }))
            // ✅ Filter 1: Remove DB cats whose NAME matches a hardcoded category
            .filter(c => !HARDCODED_CATEGORY_NAMES.has(c.name_en.toLowerCase().trim()))
            // ✅ Filter 2: Remove DB cats where ALL services already exist in hardcoded cats
            .filter(c => {
              const dbSvcs = (c.services_en || []).map(s => s.toLowerCase().trim())
              if (dbSvcs.length === 0) return false
              const allExist = dbSvcs.every(s => HARDCODED_SERVICES.has(s))
              return !allExist
            })
            // ✅ Filter 3: Remove individual service names masquerading as categories
            .filter(c => (c.services_en || []).length > 1)

          const nonEmpty = dbCats.filter(c => (c.services_en || []).length > 0)
          setCategories([...SERVICE_CATEGORIES, ...nonEmpty])
        }
      })
    supabase.from('services').select('*').order('category').order('name')
      .then(({ data, error }) => { if (!error && data) setDbServices(data) })
  }, [])

  // ── CART ACTIONS ────────────────────────────────────────────
  function addToCart(catId, svcName) {
    const id = `${catId}::${svcName}`
    setCart(prev => prev.find(i => i.id === id) ? prev : [...prev, { id, name: svcName, categoryId: catId }])
  }
  function removeFromCart(id) {
    setCart(prev => prev.filter(i => i.id !== id))
  }
  function isInCart(catId, svcName) {
    return cart.some(i => i.id === `${catId}::${svcName}`)
  }
  function toggleCat(id) {
    setOpenCats(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // ── REVIEW QUOTE ────────────────────────────────────────────
  function reviewQuote() {
    sessionStorage.setItem('lca_quote_cart', JSON.stringify(cart))
    navigate('/book?mode=quote')
  }

  return (
    <div className="svc-page">
      <style>{STYLES}</style>

      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* NAV */}
      <nav className={`svc-nav${scrolled ? ' scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <Link to="/" className="svc-nav-brand" aria-label="Loan Closing Agent — Home">
          <span className="svc-nav-name">Loan Closing Agent</span>
          <span className="svc-nav-sub" aria-hidden="true">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
        </Link>
        <div className="svc-nav-links">
          <Link to="/services" className="svc-nav-link active" aria-current="page">{isEs ? 'Servicios' : 'Services'}</Link>
          <Link to="/about"    className="svc-nav-link">{isEs ? 'Nosotros' : 'About'}</Link>
          <Link to="/contact"  className="svc-nav-link">{isEs ? 'Contacto' : 'Contact'}</Link>
          <Link to="/login"    className="svc-nav-link" style={{ color:'#C9A84C', fontWeight:600 }}>{isEs ? 'Iniciar Sesión' : 'Sign In'}</Link>
        </div>
      </nav>

      {/* HERO */}
      <header role="banner">
        <section className="svc-hero" aria-labelledby="hero-heading">
          <p className="svc-hero-eyebrow" aria-hidden="true">
            {isEs ? 'CONSTRUYE TU COTIZACIÓN' : 'Need a notary? We come to you — anywhere in the U.S.'}
          </p>
          <div className="svc-hero-rule" aria-hidden="true" />
          <h1 id="hero-heading">
            {isEs
              ? <>Servicios Notariales Profesionales:<br /><span>Construye tu Cotización en Segundos</span></>
              : <>Same-Day, Stress-Free<br /><span>Appointments Available.</span></>
            }
          </h1>
          <p className="svc-hero-sub">
            {isEs
              ? 'Ejecute sus documentos de forma segura en cualquier lugar de los EE. UU. Todas las cotizaciones son todo incluido, sin tarifas ocultas.'
              : 'Simply select the service or services for your instant, all-inclusive quote with no hidden fees. What you see is what you pay — and that\'s it!'}
          </p>

          {/* PATHWAYS */}
          <div className="svc-paths" role="group" aria-label={isEs ? 'Opciones de inicio' : 'Getting started options'}>
            <div className="svc-path-card">
              <h3 className="svc-path-h3">{isEs ? '¿Cliente Nuevo?' : 'New Customer?'}</h3>
              <button
                className="svc-path-btn guest"
                onClick={() => document.getElementById('service-categories')?.scrollIntoView({ behavior:'smooth' })}
                aria-label={isEs ? 'Comenzar a construir una cotización como invitado' : 'Start building a notary quote as a guest'}>
                {isEs ? 'Iniciar Cotización →' : 'Start Guest Quote →'}
              </button>
            </div>
            <div className="svc-path-card">
              <h3 className="svc-path-h3">{isEs ? '¿Cliente Frecuente?' : 'Returning Customer?'}</h3>
              <Link
                to="/login"
                className="svc-path-btn signin"
                aria-label={isEs ? 'Iniciar sesión para cargar información guardada' : 'Sign in to your account to load saved information'}>
                {isEs ? 'Iniciar Sesión →' : 'Sign In to My Account →'}
              </Link>
            </div>
          </div>

          <div style={{ marginTop:16, fontSize:11, color:'rgba(255,255,255,0.35)' }}>
            🎖️{' '}
            <img src="/veteran-badge.png" alt="Official seal certifying Loan Closing Agent LLC as a Veteran and Hispanic Owned business." style={{ display:'none' }} />
            {isEs ? 'Empresa certificada de propiedad veterana e hispana' : 'Certified Veteran & Hispanic Owned Business'}
          </div>
        </section>
      </header>

      {/* MAIN CONTENT */}
      <main id="main-content" tabIndex={-1}>
        <div className="svc-main">

          <p className="svc-instruction" id="categories-instruction">
            {isEs
              ? 'Selecciona una categoría para ver los servicios disponibles y agregarlos a tu cotización.'
              : 'Select a category below to view available services and add them to your quote.'}
          </p>

          <section id="service-categories" aria-labelledby="categories-heading" aria-describedby="categories-instruction">
            <h2 id="categories-heading" className="sr-only">
              {isEs ? 'Categorías de Servicios Notariales' : 'Notary Service Categories'}
            </h2>

            {categories.map(cat => {
              const isOpen  = !!openCats[cat.id]
              const name    = isEs ? cat.name_es   : cat.name_en
              const desc    = isEs ? cat.desc_es   : cat.desc_en
              const svcs    = isEs ? cat.services_es : cat.services_en
              const plains  = isEs ? cat.plains_es  : cat.plains_en
              const enSvcs  = cat.services_en || []

              if (!svcs || svcs.length === 0) return null

              const addedInCat = enSvcs.filter(s => isInCart(cat.id, s)).length

              return (
                <div key={cat.id} className={`svc-cat${isOpen ? ' open' : ''}`} role="region" aria-label={name}>
                  <button
                    className="svc-cat-btn"
                    onClick={() => toggleCat(cat.id)}
                    aria-expanded={isOpen}
                    aria-controls={`cat-list-${cat.id}`}
                    type="button">
                    <span className="svc-cat-icon" aria-hidden="true">{cat.icon}</span>
                    <span className="svc-cat-info">
                      <span className="svc-cat-name">{name}</span>
                      <span className="svc-cat-desc">{desc}</span>
                    </span>
                    <span className="svc-cat-count" aria-hidden="true">
                      {addedInCat > 0 ? `✓ ${addedInCat} added` : `${svcs.length} ${isEs ? 'servicios' : 'services'}`}
                    </span>
                    <span className="svc-cat-arrow" aria-hidden="true">▾</span>
                  </button>

                  <ul id={`cat-list-${cat.id}`} className="svc-list" role="list" aria-label={`${name} services`}>
                    {svcs.map((svc, j) => {
                      const enName = enSvcs[j] || svc
                      const plain  = (plains || [])[j]
                      const added  = isInCart(cat.id, enName)
                      return (
                        <li key={j} className={`svc-item${added ? ' added' : ''}`} role="listitem">
                          <span className="svc-item-body">
                            <span className="svc-item-name">{svc}</span>
                            {plain && <span className="svc-item-plain">{plain}</span>}
                          </span>
                          <button
                            className={`svc-add-btn${added ? ' added' : ' default'}`}
                            onClick={() => added ? removeFromCart(`${cat.id}::${enName}`) : addToCart(cat.id, enName)}
                            aria-label={added
                              ? `${isEs ? 'Quitar' : 'Remove'} ${svc} ${isEs ? 'de la cotización' : 'from quote'}`
                              : `${isEs ? 'Agregar' : 'Add'} ${svc} ${isEs ? 'a la cotización' : 'to quote'}`}
                            type="button">
                            {added ? (isEs ? '✓ Agregado' : '✓ Added') : (isEs ? '+ Agregar' : '+ Add to Quote')}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
          </section>

          {/* TRUST BADGES */}
          <div style={{ marginTop:40, textAlign:'center', padding:'28px 24px', background:'rgba(255,255,255,0.03)', borderRadius:14, border:'1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#C9A84C', marginBottom:12 }}>Why Choose LCA?</div>
            <div style={{ display:'flex', justifyContent:'center', gap:32, flexWrap:'wrap' }}>
              {[
                { icon:'✅', text: isEs ? 'Precios todo incluido' : 'All-inclusive pricing' },
                { icon:'🎖️', text: isEs ? 'Empresa veterana e hispana' : 'Veteran & Hispanic owned' },
                { icon:'🌎', text: isEs ? 'Cobertura nacional' : 'Nationwide coverage' },
                { icon:'⚡', text: isEs ? 'Respuesta rápida' : 'Fast response' },
              ].map((b, i) => (
                <div key={i} style={{ fontSize:12, color:'rgba(255,255,255,0.55)', display:'flex', alignItems:'center', gap:6 }}>
                  <span aria-hidden="true">{b.icon}</span>{b.text}
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>

      {/* FOOTER */}
      <footer className="svc-footer" role="contentinfo">
        <Link to="/" className="svc-footer-logo" aria-label="Loan Closing Agent LLC — Home">
          <div className="svc-footer-logo-circle">
            <img src="/lca_logo_quill_map.png" alt="Loan Closing Agent LLC logo" onError={e => { e.target.style.display='none' }} />
          </div>
          <div>
            <span className="svc-footer-brand-name">Loan Closing Agent LLC</span>
            <span className="svc-footer-brand-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </div>
        </Link>
        <nav className="svc-footer-links" aria-label="Footer navigation">
          <Link to="/services">{t('footer_services')}</Link>
          <Link to="/about">{t('footer_about')}</Link>
          <Link to="/contact">{t('footer_contact')}</Link>
          <Link to="/accessibility">{t('footer_accessibility')}</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/login">{t('footer_signin')}</Link>
        </nav>
        <div className="svc-footer-bottom">
          <p className="svc-footer-copy">{t('footer_copy')}</p>
          <p className="svc-footer-veteran">{t('footer_veteran')}</p>
          <p style={{ fontSize:10, color:'rgba(255,255,255,0.25)', marginTop:6, fontStyle:'italic' }}>
            Loan Closing Agent, LLC is not a law firm and does not provide legal advice.
          </p>
        </div>
      </footer>

      {/* STICKY CART BAR */}
      <div
        className={`svc-cart-bar${cart.length > 0 ? ' visible' : ''}`}
        role="region"
        aria-label={isEs ? 'Resumen de cotización' : 'Quote cart summary'}
        aria-live="polite">
        <div className="svc-cart-bar-inner">
          <div className="svc-cart-info">
            <div className="svc-cart-count" aria-label={`${cart.length} ${isEs ? 'servicios seleccionados' : 'services selected'}`}>
              {cart.length}
            </div>
            <span className="svc-cart-text">
              <strong>{cart.length} {isEs ? (cart.length === 1 ? 'servicio' : 'servicios') : (cart.length === 1 ? 'service' : 'services')}</strong>
              {' '}{isEs ? 'agregado(s) a tu cotización' : 'added to your quote'}
            </span>
          </div>
          <button
            className="svc-cart-review-btn"
            onClick={reviewQuote}
            aria-label={`${isEs ? 'Revisar cotización con' : 'Review quote with'} ${cart.length} ${isEs ? 'servicios' : 'services'}`}>
            {isEs ? 'Revisar Cotización →' : 'Review My Quote →'}
          </button>
        </div>
      </div>

      {/* BACK TO TOP */}
      <button
        className={`svc-backtop${showTop ? ' show' : ''}${cart.length > 0 ? ' cart-visible' : ''}`}
        onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}
        aria-label={isEs ? 'Volver arriba' : 'Back to top'}>
        ↑
      </button>
    </div>
  )
}
